/**
 * Invite User Modal Component
 * 
 * Modal for inviting new team members with role and account access configuration
 * Part of E007-S026: Team Management
 */

import React, { useState } from 'react';
import { InviteUserRequest, InviteUserResponse } from '../../types/team';

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (response: InviteUserResponse) => void;
  onInvite: (data: InviteUserRequest) => Promise<InviteUserResponse>;
}

const ROLE_DESCRIPTIONS = {
  admin: 'Full access to all features and settings',
  manager: 'Can manage campaigns and users',
  analyst: 'Can view and analyze data',
  viewer: 'Read-only access to dashboards',
};

export const InviteUserModal: React.FC<InviteUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onInvite,
}) => {
  const [formData, setFormData] = useState<InviteUserRequest>({
    email: '',
    full_name: '',
    role: 'viewer',
    account_access: [],
  });
  
  const [showInviteToken, setShowInviteToken] = useState(false);
  const [inviteResponse, setInviteResponse] = useState<InviteUserResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mock accounts list (in real app, fetch from API)
  const availableAccounts = [
    { id: 'meta-1', name: 'Meta - GoodPower Main', platform: 'meta' as const },
    { id: 'google-1', name: 'Google Ads - Brand Campaign', platform: 'google' as const },
    { id: 'tiktok-1', name: 'TikTok - Gen Z Outreach', platform: 'tiktok' as const },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Validation
    if (!formData.email || !formData.full_name) {
      setError('Email and full name are required');
      setLoading(false);
      return;
    }

    if (formData.account_access.length === 0) {
      setError('Please select at least one account with view permission');
      setLoading(false);
      return;
    }

    try {
      const response = await onInvite(formData);
      setInviteResponse(response);
      setShowInviteToken(true);
      onSuccess(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountToggle = (accountId: string) => {
    const existingIndex = formData.account_access.findIndex(a => a.account_id === accountId);
    
    if (existingIndex >= 0) {
      // Remove account
      setFormData({
        ...formData,
        account_access: formData.account_access.filter(a => a.account_id !== accountId),
      });
    } else {
      // Add account with default view permission
      setFormData({
        ...formData,
        account_access: [
          ...formData.account_access,
          { account_id: accountId, can_view: true, can_edit: false, can_delete: false },
        ],
      });
    }
  };

  const handlePermissionChange = (accountId: string, permission: 'can_view' | 'can_edit' | 'can_delete', value: boolean) => {
    setFormData({
      ...formData,
      account_access: formData.account_access.map(access =>
        access.account_id === accountId
          ? { ...access, [permission]: value }
          : access
      ),
    });
  };

  const handleClose = () => {
    setFormData({ email: '', full_name: '', role: 'viewer', account_access: [] });
    setShowInviteToken(false);
    setInviteResponse(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  // Show success screen with invite token
  if (showInviteToken && inviteResponse) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-4 text-green-600">✓ Invitation Sent!</h2>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                Invitation sent to <strong>{formData.email}</strong>
              </p>
              
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invite Token (share with user):
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={inviteResponse.invite_token}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
                  />
                  <button
                    onClick={() => navigator.clipboard.writeText(inviteResponse.invite_token)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Copy
                  </button>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Expires: {new Date(inviteResponse.invite_expires_at).toLocaleDateString()}
                </p>
              </div>

              {inviteResponse.invite_sent && (
                <p className="text-sm text-green-600 mb-2">
                  ✓ Email invitation sent successfully
                </p>
              )}
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show invitation form
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-6">Invite Team Member</h2>

          <form onSubmit={handleSubmit}>
            {/* Email */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="user@example.com"
              />
            </div>

            {/* Full Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                required
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="John Doe"
              />
            </div>

            {/* Role Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role *
              </label>
              <div className="space-y-2">
                {(Object.keys(ROLE_DESCRIPTIONS) as Array<keyof typeof ROLE_DESCRIPTIONS>).map((role) => (
                  <label key={role} className="flex items-start gap-3 p-3 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="role"
                      value={role}
                      checked={formData.role === role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 capitalize">{role}</div>
                      <div className="text-sm text-gray-600">{ROLE_DESCRIPTIONS[role]}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Account Access */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Account Access *
              </label>
              <div className="space-y-3">
                {availableAccounts.map((account) => {
                  const access = formData.account_access.find(a => a.account_id === account.id);
                  const isSelected = !!access;

                  return (
                    <div key={account.id} className="border border-gray-200 rounded-md p-3">
                      <label className="flex items-center gap-2 mb-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleAccountToggle(account.id)}
                        />
                        <span className="font-medium">{account.name}</span>
                        <span className="text-xs text-gray-500 uppercase">{account.platform}</span>
                      </label>

                      {isSelected && (
                        <div className="ml-6 flex gap-4 text-sm">
                          <label className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              checked={access.can_view}
                              onChange={(e) => handlePermissionChange(account.id, 'can_view', e.target.checked)}
                            />
                            <span>View</span>
                          </label>
                          <label className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              checked={access.can_edit}
                              onChange={(e) => handlePermissionChange(account.id, 'can_edit', e.target.checked)}
                            />
                            <span>Edit</span>
                          </label>
                          <label className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              checked={access.can_delete}
                              onChange={(e) => handlePermissionChange(account.id, 'can_delete', e.target.checked)}
                            />
                            <span>Delete</span>
                          </label>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
